import React, {
  useState, useMemo, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Loader from 'eventtia-ui-components/lib/Loader';
import Message from 'eventtia-ui-components/lib/Message';
import callApi from '../../actions/callApi';
import { setActiveAttendee } from '../../actions/app';
import PersonCard from '../../components/PersonCard';
import ListViewTable from '../../components/ListViewTable';
import PeopleShow from '../PeopleShow';
import {
  getModuleByType,
  getCurrentAttendee,
  getCurrentParticipant,
  getModuleNames,
  getCurrentPerson,
} from '../../helpers/getters';
import InfiniteScroll from '../../components/InfiniteScroll/SimplebarInfiniteScroll';
import FilterActions from '../../components/FilterActions';
// import FilterUI from '../../components/FilterWithSelect';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import getMeetingVisibility from '../../helpers/meetings';
// import getDistinctIds from '../../helpers/getDistinctIds';
import { showInEventTimezone } from '../../helpers/dates';
import SubpageTitle from '../../components/SubpageTitle';
import TabletExitButton from '../../components/TabletExitButton';
import useEventUriHack from '../../hooks/hacks/useEventUriHack';
import { EVENT_URI } from '../../helpers/constants';

const useStyles = makeStyles((theme) => ({
  content: {
    minWidth: 320,
    padding: theme.spacing(
      theme.subpageDrawer.paddings.desktop.vertical,
      theme.subpageDrawer.paddings.desktop.horizontal
    ),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(
        theme.subpageDrawer.paddings.mobile.vertical,
        theme.subpageDrawer.paddings.mobile.horizontal
      ),
    },
  },
  message: {
    position: 'absolute',
    top: 32,
    left: 52,
    right: 52,
    zIndex: 2000,
    whiteSpace: 'pre-wrap',
  },
  container: {
    margin: 0,
    padding: 0,
    overflowY: 'hidden',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      maxWidth: '100%',
    },
  },
  showContainer: {
    backgroundColor: theme.palette.background.light,
    padding: theme.spacing(
      theme.subpageDrawer.paddings.desktop.vertical,
      theme.subpageDrawer.paddings.desktop.horizontal
    ),
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 3,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(
        theme.subpageDrawer.paddings.mobile.vertical,
        theme.subpageDrawer.paddings.mobile.horizontal
      ),
    },
  },
  moduleName: {
    padding: theme.spacing(1.5, 0),
    color: theme.palette.darkGrey.main,
    fontWeight: 'bold',
  },
  moduleDescription: {
    color: theme.palette.darkGrey.light,
  },
  search: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: theme.spacing(3, 0),
    color: theme.palette.primary.main,
  },
  filters: {
    display: 'flex',
    marginBottom: theme.spacing(4),
    alignItems: 'flex-end',
  },
  header: {
    padding: 0,
    [theme.breakpoints.up('lg')]: {
      position: 'sticky',
      backgroundColor: theme.palette.background.light,
      zIndex: 2,
      top: theme.spacing(-theme.subpageDrawer.paddings.desktop.vertical),
      padding: theme.spacing(
        theme.subpageDrawer.paddings.desktop.vertical,
        theme.subpageDrawer.paddings.desktop.horizontal
      ),
      margin: theme.spacing(
        -theme.subpageDrawer.paddings.desktop.vertical,
        -theme.subpageDrawer.paddings.desktop.horizontal,
        theme.subpageDrawer.paddings.desktop.vertical
      ),
    },
  },
  cardsContainer: {
    marginTop: theme.spacing(7.5),
    overflow: 'hidden',
    // padding: theme.spacing(0, 4.5),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(5),
      padding: theme.spacing(0, 3, 0, 3),
      gap: theme.spacing(5),
    },
  },
  cardsContainerList: {
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100% + 56px)',
      marginLeft: theme.spacing(-3.5),
    },
  },
  filtersWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  dataTitle: {
    fontSize: 14,
    color: theme.palette.darkGrey.light,
  },
  singleAttendeeLoader: {
    zIndex: 2,
  },
  icon: {
    color: theme.palette.primary.main,
  },
  cardWrapper: {
    marginBottom: theme.spacing(5),
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3.75),
    },
  },
}));

const People = ({
  entities,
  isFetchingAttendees,
  attendeesError,
  isFetchingSingleAttendee,
  singleAttendeeError,
  links,
  attendeesOrder,
  attendees,
  businessConferences,
  businessConferenceParticipants,
  attendeeTypeCustomFields,
  ratings,
  denyList,
  recommendations,
  callApi: dispatchCallApi,
  attendeeTypes,
  businessConferenceParticipantTypes: participantTypes,
  appSettings,
  activeAttendeeId,
  setActiveAttendee: dispatchSetActiveAttendee,
  scrollToTop,
  broadcasts,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('attendees');
  const sortAttendeesByLastName = useEventUriHack('sortAttendeesByLastName');
  const [sort, setSort] = useState(!sortAttendeesByLastName ? '' : 'last_name asc');
  const [listView, setListView] = useState(false);
  const [message, setMessage] = useState();
  const moduleTitle = getModuleNames(appSettings, 'Attendees');

  const currentAttendee = getCurrentAttendee(entities);
  const currentParticipant = getCurrentParticipant(entities);

  const [settings] = Object.values(appSettings);
  const attendeesModule = getModuleByType(settings.modules, 'Attendees');
  const chatEnabledFor = attendeesModule?.customParams?.chatEnabledParticipantTypes || [];
  const showAllAttendees = (
    attendeesModule?.customParams?.attendeesListEnabled || []
  ).includes(currentAttendee.attendeeType.id);
  const offsetToUtc = !showInEventTimezone(settings);

  const [conference] = Object.values(businessConferences);
  const { automaticScheduling, blacklistEnabled, showOnlyConfirmed = true } = conference || {};

  const currentParticipantId = currentParticipant?.id;
  const businessConferenceId = currentParticipant?.businessConference?.id;
  const currentParticipantType = participantTypes[currentParticipant?.participantTypeId];
  const canRequestMeetingTo = currentParticipantType?.canRequestMeetingToAttendeeTypes;

  const order = attendeesOrder;
  // const order = useMemo(() => {
  //   const currentPersonId = getCurrentAttendee(entities)?.id;
  //   return attendeesOrder?.filter((id) => id !== currentPersonId);
  // }, [attendeesOrder, entities]);

  const attendeeTypesIdsToShow = ['175309', '167560', '167561', '167563', '167564', '167565', '175300', '175301', '175302', '175304', '175303', '175305', '175306', '175308', '175323'];
  const stringAttendeeTypesParam = useMemo(() => {
    const attendeeTypesToShow = Object.keys(attendeeTypes).filter(
      (attendeeTypeId) => (attendeeTypesIdsToShow.includes(attendeeTypeId))
    );
    return (attendeeTypesToShow.length > 1) ? attendeeTypesToShow.join(',') : '';
  //   if (showAllAttendees) return '';
  //   if (!businessConferenceId || !canRequestMeetingTo) return chatEnabledFor.join(',');
  //   return getDistinctIds(!chatEnabledFor.includes(currentAttendee.attendeeType.id),
  //     canRequestMeetingTo, chatEnabledFor).join(',');
  }, [
    businessConferenceId, canRequestMeetingTo,
    chatEnabledFor, currentAttendee.attendeeType.id, showAllAttendees,
    attendeeTypes,
  ]);

  const fetchParams = useMemo(() => {
    const values = {
      eventUri: EVENT_URI,
      businessConferenceId,
      sort,
      offsetToUtc,
      showOnlyConfirmed,
    };
    if (stringAttendeeTypesParam) values.attendeeTypes = stringAttendeeTypesParam;
    return values;
  }, [
    stringAttendeeTypesParam, businessConferenceId, sort, offsetToUtc, showOnlyConfirmed,
  ]);

  useEffect(() => {
    dispatchCallApi('attendees', fetchParams);
  }, [dispatchCallApi, fetchParams]);

  useEffect(() => {
    if (currentParticipantId) {
      dispatchCallApi('recommendedParticipants', {
        businessConferenceId,
        currentParticipantId,
      });
      if (automaticScheduling) {
        dispatchCallApi('ratings', {
          businessConferenceId,
        });
        if (blacklistEnabled) dispatchCallApi('denyList', { businessConferenceId });
      }
    }
  }, [
    dispatchCallApi, businessConferenceId, currentParticipantId,
    automaticScheduling, blacklistEnabled,
  ]);

  const activeAttendee = attendees[activeAttendeeId] && getCurrentPerson(
    attendees[activeAttendeeId], businessConferenceParticipants, attendeeTypes,
    attendeeTypeCustomFields
  );
  const shouldFetchSingleAttendee = !isFetchingAttendees && !isFetchingSingleAttendee
    && !singleAttendeeError && !attendeesError && activeAttendeeId && Number(activeAttendeeId)
    && !activeAttendee;
  useEffect(() => {
    if (shouldFetchSingleAttendee) dispatchCallApi('singleAttendee', { id: activeAttendeeId, offsetToUtc });
  }, [
    dispatchCallApi, activeAttendeeId, shouldFetchSingleAttendee, offsetToUtc,
  ]);

  const { inSchedulingTime } = getMeetingVisibility(entities);
  const ratingEnabled = conference?.automaticScheduling && inSchedulingTime;

  useEffect(() => {
    dispatchCallApi('attendeeTypes');
  }, [dispatchCallApi]);

  const fetchNextPageIfExists = () => {
    const { next } = links;
    if (!activeAttendeeId && next) dispatchCallApi(
      'attendees',
      fetchParams,
      next
    );
  };

  return (
    <>
      {!activeAttendeeId && (
        <TabletExitButton />
      )}
      <Loader
        className={classes.singleAttendeeLoader}
        variant="absolute"
        loading={isFetchingSingleAttendee}
      />
      {activeAttendee && (
        <div className={classes.showContainer}>
          <PeopleShow
            currentAttendee={currentAttendee}
            currentParticipant={currentParticipant}
            person={activeAttendee}
            attendeeTypeCustomFields={attendeeTypeCustomFields}
            scrollToTop={scrollToTop}
            broadcasts={broadcasts}
          />
        </div>
      )}
      <Container className={classes.container}>
        <Message
          className={classes.message}
          type={message?.type}
          title={
            message?.type === 'error' ? t('meeting:meeting.error') : t('meeting:meeting.requestSent')
          }
          message={message?.text}
          onClose={() => setMessage()}
        />
        <InfiniteScroll
          loading={isFetchingAttendees}
          error={attendeesError}
          fetchNextPageIfExists={fetchNextPageIfExists}
        >
          <div className={classes.content}>
            <div className={classes.header}>
              <SubpageTitle text={moduleTitle} />
              <FilterActions
                entity="attendees"
                fetchParams={fetchParams}
                sort={sort}
                setSort={setSort}
                setListView={setListView}
                listView={listView}
              />
              {/* <FilterUI
                entity="attendees"
                fetchParams={fetchParams}
                sort={sort}
                setSort={setSort}
              /> */}
            </div>
            <Grid
              container
              spacing={2}
              className={clsx(classes.cardsContainer, listView && classes.cardsContainerList)}
            >
              {(listView && order) ? (
                <ListViewTable
                  setMessage={setMessage}
                  ratingEnabled={ratingEnabled}
                  order={order}
                  broadcasts={broadcasts}
                />
              ) : (
                <>
                  {order?.map((attendeeId) => {
                    const currentPerson = getCurrentPerson(
                      attendees[attendeeId], businessConferenceParticipants, attendeeTypes,
                      attendeeTypeCustomFields, ratings, denyList, recommendations
                    );
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={6}
                        key={attendeeId}
                        className={classes.cardWrapper}
                      >
                        <PersonCard
                          person={currentPerson}
                          onBlockClick={() => dispatchSetActiveAttendee(attendeeId)}
                          // showChatButton
                          showContactInfo
                        />
                      </Grid>
                    );
                  })}
                </>
              )}
            </Grid>
          </div>
        </InfiniteScroll>
      </Container>
    </>
  );
};

People.propTypes = {
  callApi: PropTypes.func.isRequired,
  entities: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  attendees: PropTypes.objectOf(
    CustomPropTypes.attendee
  ),
  businessConferences: PropTypes.objectOf(
    CustomPropTypes.conference
  ),
  businessConferenceParticipants: PropTypes.objectOf(
    CustomPropTypes.participant
  ),
  attendeeTypeCustomFields: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string,
    })
  ),
  isFetchingAttendees: PropTypes.bool,
  isFetchingSingleAttendee: PropTypes.bool,
  attendeeTypes: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  businessConferenceParticipantTypes: PropTypes.objectOf(
    CustomPropTypes.participantType
  ),
  attendeesError: PropTypes.string,
  singleAttendeeError: PropTypes.string,
  attendeesOrder: PropTypes.arrayOf(
    PropTypes.string
  ),
  links: PropTypes.shape({
    next: PropTypes.string,
  }),
  appSettings: CustomPropTypes.appSettings.isRequired,
  activeAttendeeId: PropTypes.string,
  setActiveAttendee: PropTypes.func.isRequired,
  ratings: PropTypes.objectOf(CustomPropTypes.rating),
  denyList: PropTypes.objectOf(CustomPropTypes.deniedParticipant),
  recommendations: PropTypes.arrayOf(CustomPropTypes.recommendation),
  scrollToTop: PropTypes.func.isRequired,
  broadcasts: PropTypes.objectOf(PropTypes.func).isRequired,
};

People.defaultProps = {
  attendees: {},
  attendeeTypes: {},
  businessConferences: {},
  businessConferenceParticipantTypes: {},
  businessConferenceParticipants: {},
  attendeeTypeCustomFields: {},
  isFetchingAttendees: false,
  isFetchingSingleAttendee: false,
  attendeesError: undefined,
  singleAttendeeError: undefined,
  attendeesOrder: undefined,
  links: {},
  activeAttendeeId: undefined,
  ratings: {},
  denyList: {},
  recommendations: [],
};

const mapStateToProps = ({
  entities,
  entities: {
    attendeeTypeCustomFields,
    attendees,
    attendeeTypes,
    businessConferences,
    businessConferenceParticipantTypes,
    businessConferenceParticipants,
    businessConferenceRatedPossibleMeetings: ratings,
    businessConferenceParticipantBlacklists: denyList,
    appSettings,
  },
  meta: {
    recommendations,
  },
  fetchStatus: {
    attendees: {
      isFetching: isFetchingAttendees,
      error: attendeesError,
      links,
      order: attendeesOrder,
    },
    singleAttendee: {
      isFetching: isFetchingSingleAttendee,
      error: singleAttendeeError,
    },
  },
  app: {
    activeSubpage: {
      activeAttendeeId,
    },
  },
}) => ({
  attendeeTypeCustomFields,
  attendeeTypes,
  businessConferenceParticipantTypes,
  isFetchingAttendees,
  attendeesError,
  isFetchingSingleAttendee,
  singleAttendeeError,
  links,
  attendeesOrder,
  entities,
  attendees,
  businessConferences,
  businessConferenceParticipants,
  appSettings,
  activeAttendeeId,
  ratings,
  denyList,
  recommendations,
});

export default connect(mapStateToProps,
  { setActiveAttendee, callApi })(People);
